// The two day-tracker cards — Milk and Sleep — side by side. Each shows the
// day's running total and the next expected event. Tapping a card opens a
// confirm popup that logs a feed, starts a nap, or ends the current nap.

import { useState } from "react";
import type { PlanEvent } from "../lib/dayPlan";
import type { AgeBand } from "../data/tcbSchedule";
import { EventIcon } from "./icons";
import { hm, durStr, minutesFromMidnight } from "../lib/timeFmt";
import { useT, useAmount } from "../lib/LocaleContext";

type Props = {
  /** A sleep currently in progress, if any. */
  liveSleep: PlanEvent | null;
  /** Next upcoming feed/nurse. */
  nextFeed: PlanEvent | null;
  /** Next upcoming nap/bedtime. */
  nextSleep: PlanEvent | null;
  /** Today's running totals. */
  intakeOz: number;
  feedCount: number;
  sleepMinutes: number;
  napCount: number;
  babyName: string;
  band: AgeBand;
  onLogFeed: () => void;
  onStartNap: () => void;
  onEndNap: () => void;
};

type Pending = "feed" | "start-nap" | "end-nap" | null;

export function TrackCards({
  liveSleep,
  nextFeed,
  nextSleep,
  intakeOz,
  feedCount,
  sleepMinutes,
  napCount,
  babyName,
  band,
  onLogFeed,
  onStartNap,
  onEndNap,
}: Props) {
  const [pending, setPending] = useState<Pending>(null);
  const t = useT();
  const amount = useAmount();

  // A sleep that began before midnight has its plan `start` clipped to 0 so the
  // ribbon draws right; the card still says the real time it started ("since
  // 11:40pm"), not "since 12:00am".
  const liveSleepStartMin = liveSleep
    ? liveSleep.actualStartAt
      ? minutesFromMidnight(new Date(liveSleep.actualStartAt))
      : liveSleep.start
    : 0;

  const fp = nextFeed?.predicted;
  const feedQuality = fp?.amountRange
    ? t.trackCards.tryRange(amount.range(fp.amountRange[0], fp.amountRange[1]))
    : fp?.amountOz != null
      ? t.trackCards.tryAmount(amount.fmt(fp.amountOz))
      : t.trackCards.feedOnCues;
  // Cold-start guidelines — shown instead of a bare "—" when there's no history.
  const feedGuideline = t.trackCards.feedGuideline(band.feedIntervalHrsMin, band.feedIntervalHrsMax);
  const wakeGuideline = t.trackCards.wakeGuideline(band.wakeWindowMin, band.wakeWindowMax);

  const confirm: Record<Exclude<Pending, null>, { title: string; body: string; cta: string; run: () => void }> = {
    feed: {
      title: t.trackCards.confirmFeedTitle(babyName),
      body: t.trackCards.confirmFeedBody,
      cta: t.trackCards.confirmFeedCta,
      run: onLogFeed,
    },
    "start-nap": {
      title: t.trackCards.confirmStartNapTitle(babyName),
      body: t.trackCards.confirmStartNapBody,
      cta: t.trackCards.confirmStartNapCta,
      run: onStartNap,
    },
    "end-nap": {
      title: t.trackCards.confirmEndNapTitle(babyName),
      body: t.trackCards.confirmEndNapBody,
      cta: t.trackCards.confirmEndNapCta,
      run: onEndNap,
    },
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.grid}>
        <button
          style={{ ...styles.card, borderColor: "var(--bh-feed-soft)" }}
          onClick={() => setPending("feed")}
        >
          <div style={{ ...styles.label, color: "var(--bh-feed)" }}>
            <EventIcon kind="feed" size={14} /> {t.trackCards.milk}
          </div>
          <div className="bh-num" style={styles.total}>
            {t.trackCards.ozAmount(amount.fmt(intakeOz))}
          </div>
          <div style={styles.sub}>
            {t.trackCards.feedsToday(feedCount)}
          </div>
          <div style={styles.next}>
            <span style={styles.nextLabel}>{t.trackCards.nextFeed}</span>
            <span className="bh-num" style={styles.nextValue}>
              {fp ? hm(fp.start) : feedGuideline}
            </span>
            <span style={styles.nextMeta}>{fp ? feedQuality : t.trackCards.feedOnHerCues}</span>
          </div>
        </button>

        <button
          style={{ ...styles.card, borderColor: "var(--bh-sleep-soft)" }}
          onClick={() => setPending(liveSleep ? "end-nap" : "start-nap")}
        >
          <div style={{ ...styles.label, color: "var(--bh-sleep)" }}>
            <EventIcon kind="sleep" size={14} /> {t.trackCards.sleep}
          </div>
          <div className="bh-num" style={styles.total}>
            {durStr(sleepMinutes)}
          </div>
          <div style={styles.sub}>
            {t.trackCards.napsToday(napCount)}
          </div>
          <div style={styles.next}>
            {liveSleep ? (
              <>
                <span style={{ ...styles.nextLabel, color: "var(--bh-sleep)", fontWeight: 700 }}>
                  {t.trackCards.sleepingNow}
                </span>
                <span className="bh-num" style={styles.nextValue}>
                  {t.trackCards.since(hm(liveSleepStartMin))}
                </span>
                <span style={styles.nextMeta}>{t.trackCards.tapToEnd}</span>
              </>
            ) : (
              <>
                <span style={styles.nextLabel}>{nextSleep?.kind === "bedtime" ? t.trackCards.nextBedtime : t.trackCards.nextNap}</span>
                <span className="bh-num" style={styles.nextValue}>
                  {nextSleep?.predicted ? hm(nextSleep.predicted.start) : wakeGuideline}
                </span>
                <span style={styles.nextMeta}>{t.trackCards.tapToStart}</span>
              </>
            )}
          </div>
        </button>
      </div>

      {pending && (
        <ConfirmPopup
          title={confirm[pending].title}
          body={confirm[pending].body}
          cta={confirm[pending].cta}
          onConfirm={() => {
            confirm[pending].run();
            setPending(null);
          }}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
}

function ConfirmPopup({
  title,
  body,
  cta,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  cta: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.popupTitle}>{title}</h3>
        <p style={styles.popupBody}>{body}</p>
        <div style={styles.popupActions}>
          <button style={styles.popupGhost} onClick={onClose}>
            {t.trackCards.cancel}
          </button>
          <button style={styles.popupPrimary} onClick={onConfirm}>
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { margin: "0 14px 16px" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  card: {
    appearance: "none",
    textAlign: "left",
    font: "inherit",
    cursor: "pointer",
    background: "var(--bh-surface)",
    border: "1px solid transparent",
    borderRadius: 20,
    padding: "16px 16px 14px",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
  },
  total: {
    fontSize: 32,
    fontWeight: 600,
    color: "var(--bh-ink)",
    letterSpacing: "-0.02em",
    lineHeight: 1,
    marginTop: 12,
  },
  sub: { fontSize: 13, color: "var(--bh-ink-3)", marginTop: 5 },
  next: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px solid var(--bh-divider)",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  nextLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--bh-ink-3)",
  },
  nextValue: { fontSize: 18, fontWeight: 600, color: "var(--bh-ink)" },
  nextMeta: { fontSize: 13, color: "var(--bh-ink-3)" },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  popup: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 380,
    borderRadius: 24,
    padding: 22,
  },
  popupTitle: {
    fontSize: 20,
    fontWeight: 600,
    margin: 0,
    color: "var(--bh-ink)",
    letterSpacing: "-0.01em",
  },
  popupBody: {
    fontSize: 15,
    lineHeight: 1.5,
    color: "var(--bh-ink-2)",
    margin: "10px 0 20px",
  },
  popupActions: { display: "flex", gap: 10 },
  popupGhost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 54,
  },
  popupPrimary: {
    flex: 1,
    background: "var(--bh-ink)",
    color: "var(--bh-surface)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 54,
  },
};
